import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Pencil, MoreHorizontal, Pin, PinOff, Archive, ArchiveRestore,
  Copy, Trash2, FileSpreadsheet, ListChecks, Users, Receipt, BarChart3,
  Calendar, ChevronDown, ChevronRight, ExternalLink, DollarSign, Plus, X, Save,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, ReferenceLine, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { api, apiErrorMessage } from "../../api";
import { useToast } from "../../hooks/use-toast";
import ProjectTasksModal from "../../components/app/ProjectTasksModal";

const BILLING_LABEL = {
  time_materials: "Time & Materials",
  fixed_fee: "Fixed Fee",
  retainer: "Retainer",
  non_billable: "Non-Billable",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { toast } = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("tasks");
  const [chartMode, setChartMode] = useState("progress"); // progress | hours
  const [tasksOpen, setTasksOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setDetail(await api.projectDetail(id)); }
    catch { setDetail(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="p-6 text-sm text-[#6a736f]" data-testid="project-detail-loading">Loading…</div>;
  if (!detail) return (
    <div className="p-6" data-testid="project-detail-missing">
      <Link to="/app/projects" className="text-coral text-sm inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to Projects</Link>
      <div className="mt-4 text-sm text-[#6a736f]">Project not found.</div>
    </div>
  );

  const { project, client, kpis, tasks, team, invoices, series, expenses = [], expenses_by_category = [] } = detail;

  const doAction = async (action) => {
    try {
      if (action === "archive") await api.archiveProject(id);
      if (action === "unarchive") await api.unarchiveProject(id);
      if (action === "pin") await api.pinProject(id);
      if (action === "duplicate") {
        const cp = await api.duplicateProject(id);
        toast({ title: "Project duplicated", description: cp.name });
        nav(`/app/projects/${cp.id}`);
        return;
      }
      if (action === "delete") {
        if (!window.confirm(`Delete "${project.name}"?`)) return;
        await api.deleteProject(id);
        toast({ title: "Project deleted" });
        nav("/app/projects");
        return;
      }
      await load();
    } catch (ex) {
      toast({ title: "Couldn't complete action", description: apiErrorMessage(ex, "") });
    }
  };

  return (
    <div data-testid="project-detail">
      {/* Header */}
      <Link to="/app/projects" data-testid="back-to-projects"
            className="text-sm text-[#6a736f] hover:text-coral inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to Projects
      </Link>
      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm text-[#6a736f]" data-testid="project-client-name">{client?.name || "—"}</div>
          <h1 className="font-display text-4xl mt-1 flex items-center gap-3 flex-wrap">
            {project.code && <span className="text-[#6a736f] text-xl">[{project.code}]</span>}
            <span data-testid="project-name">{project.name}</span>
            <BillingPill type={project.billing_type} />
            {project.is_pinned && <span className="text-xs text-coral inline-flex items-center gap-1"><Pin size={12} /> pinned</span>}
            {project.is_archived && <span className="text-xs text-[#6a736f] inline-flex items-center gap-1"><Archive size={12} /> archived</span>}
          </h1>
          {(project.start_date || project.end_date) && (
            <div className="text-xs text-[#6a736f] mt-1 inline-flex items-center gap-1">
              <Calendar size={12} /> {project.start_date || "—"} → {project.end_date || "—"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(`/app/projects?edit=${id}`)}
                  data-testid="edit-project-btn"
                  className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
            <Pencil size={14} /> Edit project
          </button>
          <ActionsMenu project={project} onAction={doAction} />
        </div>
      </div>

      {/* Chart */}
      <div className="mt-6 bg-white border border-cream rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button onClick={() => setChartMode("progress")}
                    data-testid="chart-mode-progress"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border ${
                      chartMode === "progress"
                        ? "bg-coral text-white border-coral"
                        : "border-cream text-forest hover:bg-cream-2/50"
                    }`}>
              <BarChart3 size={12} /> Project progress
            </button>
            <button onClick={() => setChartMode("hours")}
                    data-testid="chart-mode-hours"
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border ${
                      chartMode === "hours"
                        ? "bg-coral text-white border-coral"
                        : "border-cream text-forest hover:bg-cream-2/50"
                    }`}>
              <BarChart3 size={12} /> Hours per week
            </button>
          </div>
        </div>
        <div className="h-64" data-testid="project-chart">
          {series.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#6a736f]">
              No time tracked yet — chart will populate as work is logged.
            </div>
          ) : chartMode === "progress" ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid stroke="#EAF3EE" vertical={false} />
                <XAxis dataKey="week_start" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                {kpis.budget_total > 0 && (
                  <ReferenceLine y={kpis.budget_total} stroke="#EF4444" strokeDasharray="4 4"
                                 label={{ value: `Budget: $${kpis.budget_total.toLocaleString()}`, position: "insideTopLeft", fill: "#EF4444", fontSize: 10 }} />
                )}
                <Line type="monotone" dataKey="cumulative_revenue" stroke="#143A25" strokeWidth={2} dot={{ r: 3 }} name="Cumulative revenue" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid stroke="#EAF3EE" vertical={false} />
                <XAxis dataKey="week_start" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}h`} />
                <Tooltip formatter={(v) => `${v} h`} />
                <Bar dataKey="hours" fill="#22C55E" name="Hours" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard title="Total hours" testid="kpi-hours">
          <div className="font-display text-3xl tabular-nums">{kpis.total_hours.toFixed(2)}</div>
          <div className="text-xs text-[#6a736f] mt-2 space-y-0.5">
            <div>Billable <span className="tabular-nums float-right">{kpis.billable_hours.toFixed(2)}</span></div>
            <div>Non-billable <span className="tabular-nums float-right">{kpis.nonbillable_hours.toFixed(2)}</span></div>
          </div>
        </KpiCard>

        <KpiCard title={`Budget remaining (${kpis.budget_remaining_pct}%)`} testid="kpi-budget">
          <div className={`font-display text-3xl tabular-nums ${kpis.budget_remaining < 0 ? "text-red-600" : ""}`}>
            {fmt(kpis.budget_remaining)}
          </div>
          <div className="text-xs text-[#6a736f] mt-2">Total budget <span className="tabular-nums float-right">{fmt(kpis.budget_total)}</span></div>
          {kpis.budget_total > 0 && (
            <div className="mt-2 h-1.5 bg-[#EAF3EE] rounded-full overflow-hidden">
              <div className="h-full bg-blue-500"
                   style={{ width: `${Math.min(100, (kpis.revenue / kpis.budget_total) * 100)}%` }} />
            </div>
          )}
        </KpiCard>

        <KpiCard title="Internal costs" testid="kpi-costs">
          <div className="font-display text-3xl tabular-nums">{fmt(kpis.internal_costs)}</div>
          <div className="text-xs text-[#6a736f] mt-2 space-y-0.5">
            <div>Time <span className="tabular-nums float-right">{fmt(kpis.internal_costs_time ?? kpis.internal_costs)}</span></div>
            <div>Expenses <span className="tabular-nums float-right">{fmt(kpis.internal_costs_expenses ?? 0)}</span></div>
          </div>
        </KpiCard>

        <KpiCard title="Invoiced amount" testid="kpi-invoiced">
          <div className="font-display text-3xl tabular-nums">{fmt(kpis.invoiced_amount)}</div>
        </KpiCard>

        <KpiCard title="Uninvoiced amount" testid="kpi-uninvoiced">
          <div className="font-display text-3xl tabular-nums">{fmt(kpis.uninvoiced_amount)}</div>
          {kpis.uninvoiced_amount > 0 && (
            <Link to="/app/invoices/new" className="mt-2 text-xs text-coral hover:underline inline-flex items-center gap-1">
              New invoice <ExternalLink size={10} />
            </Link>
          )}
        </KpiCard>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-cream flex items-center gap-6 text-sm">
        {[
          { k: "tasks",    label: "Tasks",   icon: ListChecks, count: tasks.length },
          { k: "team",     label: "Team",    icon: Users,      count: team.length },
          { k: "expenses", label: "Expenses",icon: DollarSign, count: expenses.length },
          { k: "invoices", label: "Invoices",icon: Receipt,    count: invoices.length },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
                    data-testid={`detail-tab-${t.k}`}
                    className={`py-3 -mb-px border-b-2 inline-flex items-center gap-1 ${
                      tab === t.k ? "border-coral text-coral font-semibold" : "border-transparent text-[#6a736f] hover:text-forest"
                    }`}>
              <Icon size={12} /> {t.label} <span className="text-[10px] text-[#6a736f]">{t.count}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {tab === "tasks" && (
          <TasksTable tasks={tasks} onManage={() => setTasksOpen(true)} />
        )}
        {tab === "team" && (
          <TeamTable team={team} />
        )}
        {tab === "expenses" && (
          <ExpensesTab
            projectId={id}
            expenses={expenses}
            byCategory={expenses_by_category}
            onChanged={load}
            toast={toast}
          />
        )}
        {tab === "invoices" && (
          <InvoicesTable invoices={invoices} />
        )}
      </div>

      {tasksOpen && (
        <ProjectTasksModal
          project={project}
          onClose={() => { setTasksOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ title, testid, children }) {
  return (
    <div className="bg-white border border-cream rounded-xl p-4" data-testid={testid}>
      <div className="text-xs text-[#6a736f] font-medium">{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function BillingPill({ type }) {
  if (!type) return null;
  const cls = {
    time_materials: "bg-blue-50 text-blue-700 border-blue-200",
    fixed_fee:      "bg-purple-50 text-purple-700 border-purple-200",
    retainer:       "bg-amber-50 text-amber-700 border-amber-200",
    non_billable:   "bg-cream-2 text-[#6a736f] border-cream",
  }[type] || "bg-cream-2 text-[#6a736f] border-cream";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>
      {BILLING_LABEL[type] || type}
    </span>
  );
}

function ActionsMenu({ project, onAction }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
              data-testid="project-actions-menu"
              className="btn-outline-forest inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium">
        Actions <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-10 w-48 bg-white border border-cream rounded-md shadow-md py-1 text-sm"
             onMouseLeave={() => setOpen(false)}>
          <MenuItem onClick={() => { setOpen(false); onAction("pin"); }}
                    icon={project.is_pinned ? PinOff : Pin}
                    testid="detail-action-pin">
            {project.is_pinned ? "Unpin" : "Pin"}
          </MenuItem>
          <MenuItem onClick={() => { setOpen(false); onAction("duplicate"); }} icon={Copy} testid="detail-action-duplicate">Duplicate</MenuItem>
          <div className="my-1 border-t border-cream" />
          {project.is_archived ? (
            <MenuItem onClick={() => { setOpen(false); onAction("unarchive"); }} icon={ArchiveRestore} testid="detail-action-unarchive">Unarchive</MenuItem>
          ) : (
            <MenuItem onClick={() => { setOpen(false); onAction("archive"); }} icon={Archive} testid="detail-action-archive">Archive</MenuItem>
          )}
          <MenuItem onClick={() => { setOpen(false); onAction("delete"); }} icon={Trash2} danger testid="detail-action-delete">Delete</MenuItem>
        </div>
      )}
    </div>
  );
}

function MenuItem({ onClick, icon: Icon, children, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`w-full text-left px-3 py-1.5 hover:bg-cream-2/60 inline-flex items-center gap-2 ${danger ? "text-red-600" : "text-forest"}`}>
      <Icon size={12} /> {children}
    </button>
  );
}

function TasksTable({ tasks, onManage }) {
  const [sort, setSort] = useState({ key: "hours", dir: "desc" });
  const sorted = [...tasks].sort((a, b) => {
    const v = a[sort.key] - b[sort.key];
    return sort.dir === "asc" ? v : -v;
  });
  const total = sorted.reduce((t, r) => ({
    hours: t.hours + r.hours, revenue: t.revenue + r.revenue, cost: t.cost + r.cost,
  }), { hours: 0, revenue: 0, cost: 0 });
  const toggle = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" });

  return (
    <div className="bg-white border border-cream rounded-xl overflow-hidden" data-testid="tasks-tab">
      <div className="flex items-center justify-between px-4 py-3 border-b border-cream">
        <div className="font-semibold">All time</div>
        <button onClick={onManage}
                data-testid="manage-project-tasks-btn"
                className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
          <ListChecks size={12} /> Manage tasks
        </button>
      </div>
      {sorted.length === 0 ? (
        <div className="p-6 text-sm text-[#6a736f] text-center">No time tracked to any task yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-cream-2/40 text-xs text-[#6a736f]">
            <tr>
              <th className="text-left px-4 py-2">Task</th>
              <SortableTh label="Hours" active={sort.key === "hours"} dir={sort.dir} onClick={() => toggle("hours")} className="text-right" />
              <SortableTh label="Billable amount" active={sort.key === "revenue"} dir={sort.dir} onClick={() => toggle("revenue")} className="text-right" />
              <SortableTh label="Costs" active={sort.key === "cost"} dir={sort.dir} onClick={() => toggle("cost")} className="text-right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {sorted.map((r) => (
              <tr key={r.task_id || r.task_name} data-testid={`task-row-${r.task_id || r.task_name}`}>
                <td className="px-4 py-2">
                  <span className="text-forest">{r.task_name}</span>
                  {!r.billable && <span className="ml-2 text-[10px] text-[#6a736f]">non-billable</span>}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.revenue)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.cost)}</td>
              </tr>
            ))}
            <tr className="bg-cream-2/40 font-semibold">
              <td className="px-4 py-2">Total</td>
              <td className="px-4 py-2 text-right tabular-nums">{total.hours.toFixed(2)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(total.revenue)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(total.cost)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function TeamTable({ team }) {
  const total = team.reduce((t, r) => ({
    hours: t.hours + r.hours, revenue: t.revenue + r.revenue, cost: t.cost + r.cost,
  }), { hours: 0, revenue: 0, cost: 0 });
  return (
    <div className="bg-white border border-cream rounded-xl overflow-hidden" data-testid="team-tab">
      {team.length === 0 ? (
        <div className="p-6 text-sm text-[#6a736f] text-center">No time tracked by anyone yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-cream-2/40 text-xs text-[#6a736f]">
            <tr>
              <th className="text-left px-4 py-2">Team member</th>
              <th className="text-right px-4 py-2">Hours</th>
              <th className="text-right px-4 py-2">Billable amount</th>
              <th className="text-right px-4 py-2">Costs</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {team.map((r) => (
              <tr key={r.user_id} data-testid={`team-row-${r.user_id}`}>
                <td className="px-4 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] text-[#6a736f]">{r.email}</div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.revenue)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.cost)}</td>
              </tr>
            ))}
            <tr className="bg-cream-2/40 font-semibold">
              <td className="px-4 py-2">Total</td>
              <td className="px-4 py-2 text-right tabular-nums">{total.hours.toFixed(2)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(total.revenue)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{fmt(total.cost)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

function InvoicesTable({ invoices }) {
  return (
    <div className="bg-white border border-cream rounded-xl overflow-hidden" data-testid="invoices-tab">
      {invoices.length === 0 ? (
        <div className="p-6 text-sm text-[#6a736f] text-center">
          No invoices for this client yet.{" "}
          <Link to="/app/invoices/new" className="text-coral font-medium">Create the first one</Link>.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-cream-2/40 text-xs text-[#6a736f]">
            <tr>
              <th className="text-left px-4 py-2">Number</th>
              <th className="text-left px-4 py-2">Issued</th>
              <th className="text-left px-4 py-2">Due</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <td className="px-4 py-2 font-mono text-xs">{inv.number}</td>
                <td className="px-4 py-2">{inv.issued}</td>
                <td className="px-4 py-2">{inv.due}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    { Paid: "bg-green-100 text-green-800", Sent: "bg-blue-100 text-blue-800",
                      Draft: "bg-cream-2 text-[#6a736f]", Overdue: "bg-red-100 text-red-800" }[inv.status]
                    || "bg-cream-2 text-[#6a736f]"
                  }`}>{inv.status}</span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(inv.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SortableTh({ label, active, dir, onClick, className }) {
  return (
    <th onClick={onClick}
        className={`px-4 py-2 cursor-pointer select-none ${className || "text-left"}`}>
      {label} {active && (dir === "asc" ? "↑" : "↓")}
    </th>
  );
}

function fmt(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
// -------------------------------------------------------------- Expenses tab
function ExpensesTab({ projectId, expenses, byCategory, onChanged, toast }) {
  const [editing, setEditing] = useState(null); // "new" | expense obj | null

  const totals = expenses.reduce((t, x) => ({
    amount: t.amount + Number(x.amount || 0),
    billable: t.billable + (x.billable ? Number(x.amount || 0) : 0),
    count: t.count + 1,
  }), { amount: 0, billable: 0, count: 0 });

  const del = async (x) => {
    if (!window.confirm(`Delete this expense (${fmt(x.amount)} — ${x.category_name})?`)) return;
    try {
      await api.deleteExpense(x.id);
      toast?.({ title: "Expense deleted" });
      await onChanged?.();
    } catch (ex) {
      toast?.({ title: "Couldn't delete", description: apiErrorMessage(ex, "") });
    }
  };

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      {/* Totals + Add button */}
      <div className="bg-white border border-cream rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-6">
          <div>
            <div className="text-xs text-[#6a736f]">Total expenses</div>
            <div className="font-display text-2xl tabular-nums" data-testid="expenses-total">{fmt(totals.amount)}</div>
          </div>
          <div>
            <div className="text-xs text-[#6a736f]">Billable</div>
            <div className="font-display text-2xl tabular-nums text-forest">{fmt(totals.billable)}</div>
          </div>
          <div>
            <div className="text-xs text-[#6a736f]">Non-billable</div>
            <div className="font-display text-2xl tabular-nums text-[#6a736f]">{fmt(totals.amount - totals.billable)}</div>
          </div>
        </div>
        <button onClick={() => setEditing("new")}
                data-testid="add-expense-btn"
                className="btn-coral inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium">
          <Plus size={14} /> Add expense
        </button>
      </div>

      {/* By category */}
      {byCategory.length > 0 && (
        <div className="bg-white border border-cream rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-cream-2/40 text-xs font-semibold text-[#6a736f]">By category</div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#EAF3EE]">
              {byCategory.map((c) => (
                <tr key={c.category_id || c.category_name} data-testid={`expense-cat-${c.category_id || c.category_name}`}>
                  <td className="px-4 py-2">{c.category_name}</td>
                  <td className="px-4 py-2 text-xs text-[#6a736f]">{c.count} item{c.count === 1 ? "" : "s"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmt(c.billable_amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#6a736f]">{fmt(c.amount - c.billable_amount)} non-billable</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmt(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Expense list */}
      <div className="bg-white border border-cream rounded-xl overflow-hidden">
        {expenses.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6a736f]" data-testid="expenses-empty">
            No expenses logged yet. Click <b>Add expense</b> to track your first spend.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-cream-2/40 text-xs text-[#6a736f]">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">Notes</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-right px-4 py-2">Billable?</th>
                <th className="px-4 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAF3EE]">
              {expenses.map((x) => (
                <tr key={x.id} data-testid={`expense-row-${x.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{x.date}</td>
                  <td className="px-4 py-2">{x.category_name}</td>
                  <td className="px-4 py-2 text-[#3f4a44]">{x.notes || "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmt(x.amount)}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      x.billable ? "bg-blue-50 text-blue-700 border border-blue-200"
                                 : "bg-cream-2 text-[#6a736f] border border-cream"
                    }`}>{x.billable ? "Billable" : "Non-billable"}</span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <button onClick={() => setEditing(x)}
                            data-testid={`edit-expense-${x.id}`}
                            className="text-forest hover:text-coral text-xs inline-flex items-center gap-1">
                      <Pencil size={10} /> Edit
                    </button>
                    <button onClick={() => del(x)}
                            data-testid={`delete-expense-${x.id}`}
                            className="text-[#6a736f] hover:text-red-600 text-xs inline-flex items-center gap-1">
                      <Trash2 size={10} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ExpenseFormModal
          projectId={projectId}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onChanged?.(); toast?.({ title: "Expense saved" }); }}
        />
      )}
    </div>
  );
}

function ExpenseFormModal({ projectId, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    category_id: initial?.category_id || "",
    amount: initial?.amount ?? "",
    billable: initial?.billable ?? true,
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.expenseCategories(false).then((c) => {
      setCategories(c);
      // Default to first category if new + no category selected yet
      if (!isEdit && !form.category_id && c.length > 0) {
        setForm((f) => ({ ...f, category_id: c[0].id }));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {
        project_id: projectId,
        category_id: form.category_id || null,
        date: form.date,
        amount: Number(form.amount),
        billable: !!form.billable,
        notes: form.notes,
      };
      if (isEdit) {
        await api.updateExpense(initial.id, payload);
      } else {
        await api.createExpense(payload);
      }
      await onSaved?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save expense."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-md p-6 space-y-4"
            data-testid="expense-form-modal">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">{isEdit ? "Edit expense" : "Add expense"}</h2>
          <button type="button" onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Date</span>
            <input type="date" required value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="ef-date"
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="text-xs font-semibold text-[#3f4a44]">Amount ($)</span>
            <input type="number" min={0} step="0.01" required value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   data-testid="ef-amount"
                   className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
          </label>
        </div>

        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Category</span>
          <select value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  data-testid="ef-category"
                  className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm bg-white">
            <option value="">(Uncategorised)</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="text-[10px] text-[#6a736f] mt-1">Manage categories in Settings → Expense categories.</div>
        </label>

        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Notes</span>
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="ef-notes"
                    className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.billable}
                 onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                 data-testid="ef-billable"
                 className="h-4 w-4 accent-coral" />
          Billable to client
        </label>

        {err && <div className="text-sm text-red-600" data-testid="ef-error">{err}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-cream">
          <button type="button" onClick={onClose} className="btn-outline-forest px-3 py-2 rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid="ef-save"
                  className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
            <Save size={14} /> {busy ? "Saving…" : (isEdit ? "Update expense" : "Add expense")}
          </button>
        </div>
      </form>
    </div>
  );
}

